import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  Share2,
  Users,
  Link as LinkIcon,
  Plus,
  CheckCircle,
  Shield,
  AlertCircle,
  Lock,
  Baby,
  MapPin,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { Pet } from '../types/pet';
import { AddFamilyMemberModal } from './family/AddFamilyMemberModal';
import { SearchFamilyMemberModal } from './family/SearchFamilyMemberModal';
import { ManualFamilyMemberModal } from './family/ManualFamilyMemberModal';
import { CreateAncestryDIDModal } from './family/CreateAncestryDIDModal';
import { ConfirmRelationshipModal } from './family/ConfirmRelationshipModal';
import { ViewFamilyMemberModal } from './family/ViewFamilyMemberModal';
import { RecordBirthModal } from './family/RecordBirthModal';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';
import { markFound } from '../../../api/pets';
import { usePets } from '../context/PetsContext';
import { usePetPhoto } from '../hooks/usePetPhoto';

interface FamilyTreeProps {
  pet: Pet;
}

interface FamilyMember {
  id: string;
  did?: string;
  ancestryDid?: string;
  name: string;
  species: string;
  breed?: string;
  birthYear?: string;
  color?: string;
  breederName?: string;
  notes?: string;
  relationship: string;
  generation: number;
  verificationType: 'verified' | 'owner-added' | 'ancestry-did';
  confidence?: 'certain' | 'estimated' | 'unknown';
  photoKey?: string;
  imageUrl?: string;
}

type PetStatusType = 'active' | 'lost' | 'deceased';

function getPetStatus(p: Pet | undefined): PetStatusType {
  if (!p) return 'active';
  if (p.isDeceased === true) return 'deceased';
  const raw = p.lostStatus ?? (p as { lost_status?: string }).lost_status ?? '';
  const lost = raw !== '' && String(raw).toLowerCase().trim() === 'lost';
  if (lost) return 'lost';
  return 'active';
}

function FamilyMemberAvatar({
  member,
  size = 'md',
  deceased = false,
}: {
  member: FamilyMember & { isCurrentPet?: boolean };
  size?: 'sm' | 'md' | 'lg';
  deceased?: boolean;
}) {
  const { signedImageUrl } = usePetPhoto({
    petId: member.id || '',
    photoKey: member.photoKey,
    imageUrl: member.imageUrl,
  });

  const sizeClasses = {
    sm: 'w-10 h-10 text-xl',
    md: 'w-12 h-12 text-2xl',
    lg: 'w-16 h-16 text-3xl',
  };

  const gradientClasses = {
    Current:
      'from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30',
    Sibling:
      'from-teal-100 to-cyan-100 dark:from-teal-900/30 dark:to-cyan-900/30',
    Parent:
      'from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30',
    Grandparent:
      'from-gray-100 to-gray-200 dark:from-gray-800/30 dark:to-gray-700/30',
  };

  let type: keyof typeof gradientClasses = 'Sibling';
  if (member.generation === -1) type = 'Parent';
  else if (member.generation === -2) type = 'Grandparent';
  else if (member.isCurrentPet) type = 'Current';

  return (
    <div
      className={`${sizeClasses[size]} bg-gradient-to-br ${gradientClasses[type]} rounded-full flex items-center justify-center overflow-hidden ${deceased ? 'opacity-85 grayscale-[0.35]' : ''}`}
    >
      {signedImageUrl ? (
        <img
          src={signedImageUrl}
          alt={member.name}
          className={`w-full h-full object-cover ${deceased ? 'grayscale-[0.4]' : ''}`}
        />
      ) : (
        <span className={deceased ? 'opacity-80' : ''}>
          {member.species?.toLowerCase() === 'dog' ? '🐕' : '🐈'}
        </span>
      )}
    </div>
  );
}

export function FamilyTree({ pet }: FamilyTreeProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    pets,
    addFamilyMember,
    removeFamilyMember,
    refreshPets,
    setSelectedPetId,
    getPetById,
    updatePet,
  } = usePets();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showManualModal, setShowManualModal] = useState(false);
  const [showAncestryDIDModal, setShowAncestryDIDModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showRecordBirthModal, setShowRecordBirthModal] = useState(false);
  const [recordBirthPet, setRecordBirthPet] = useState<Pet | null>(null);
  const [recordBirthInitialParentRole, setRecordBirthInitialParentRole] =
    useState<'mother' | 'father' | null>(null);
  const [selectedRelationship, setSelectedRelationship] = useState('');
  const [tempMemberData, setTempMemberData] = useState<{
    name: string;
    species: string;
    gender?: string;
    breed?: string;
    approximateAge?: string;
    color?: string;
    breederName?: string;
    notes?: string;
  } | null>(null);
  const [selectedMember, setSelectedMember] = useState<FamilyMember | null>(
    null
  );
  const [privacyLevel, setPrivacyLevel] = useState<
    'private' | 'family' | 'public'
  >('private');
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);

  const isDeceased = pet.isDeceased === true;
  const offspring = useMemo(
    () =>
      pets.filter(
        (p) =>
          p.id !== pet.id &&
          (p.parents?.mother === pet.did || p.parents?.father === pet.did)
      ),
    [pets, pet.id, pet.did]
  );

  // Build family tree from actual pet data
  useEffect(() => {
    const buildFamilyMembers = () => {
      const members: FamilyMember[] = [];

      // Find pet by DID in the pets array
      const findPetByDID = (did?: string): Pet | undefined => {
        if (!did) return undefined;
        return pets.find((p) => p.did === did);
      };

      const addMember = (
        did: string | undefined,
        relationship: string,
        generation: number
      ) => {
        if (!did) return;
        const foundPet = findPetByDID(did);
        if (foundPet) {
          members.push({
            id: foundPet.id,
            did: foundPet.did,
            name: foundPet.name,
            species: foundPet.species,
            breed: foundPet.breed,
            color: foundPet.color,
            relationship,
            generation,
            verificationType: 'verified',
            photoKey: foundPet.photoKey,
            imageUrl: foundPet.imageUrl,
          });
        } else {
          // If DID exists but pet not found in pets array, it's likely a member
          // that doesn't belong to the user's main pets list (e.g. owner-added ancestor)
          members.push({
            id: `external-${did}`,
            did: did,
            name: t('family.unknownPet'),
            species: 'Unknown',
            relationship,
            generation,
            verificationType: 'verified', // It has a DID so we treat it as verified/linked
          });
        }
      };

      // Add Mother
      addMember(pet.parents?.mother, 'Mother', -1);

      // Add Father
      addMember(pet.parents?.father, 'Father', -1);

      // Add Grandparents
      addMember(pet.maternalGrandmother, 'Maternal Grandmother', -2);
      addMember(pet.maternalGrandfather, 'Maternal Grandfather', -2);
      addMember(pet.paternalGrandmother, 'Paternal Grandmother', -2);
      addMember(pet.paternalGrandfather, 'Paternal Grandfather', -2);

      // Add Siblings from siblings array
      pet.siblings?.forEach((did) => {
        addMember(did, 'Sibling', 0);
      });

      // Find siblings (pets that share same mother or father) - legacy discovery
      const discoveredSiblings = pets.filter((p) => {
        if (p.id === pet.id) return false; // Not self
        if (!p.parents) return false;

        // Already added via siblings array
        if (pet.siblings?.includes(p.did)) return false;

        // Check if shares mother or father
        const sharesMother =
          pet.parents?.mother && p.parents.mother === pet.parents.mother;
        const sharesFather =
          pet.parents?.father && p.parents.father === pet.parents.father;

        return sharesMother || sharesFather;
      });

      discoveredSiblings.forEach((sibling) => {
        members.push({
          id: sibling.id,
          did: sibling.did,
          name: sibling.name,
          species: sibling.species,
          breed: sibling.breed,
          color: sibling.color,
          relationship: 'Sibling',
          generation: 0,
          verificationType: 'verified',
          photoKey: sibling.photoKey,
          imageUrl: sibling.imageUrl,
        });
      });

      return members;
    };

    setFamilyMembers(buildFamilyMembers());
  }, [pet, pets]);

  const handleAddClick = (relationship: string) => {
    setSelectedRelationship(relationship);
    setShowAddModal(true);
  };

  const handleMethodSelect = (method: 'search' | 'manual' | 'ancestry-did') => {
    setShowAddModal(false);
    if (method === 'search') {
      setShowSearchModal(true);
    } else if (method === 'manual') {
      setShowManualModal(true);
    } else {
      setShowManualModal(true); // Manual entry is required before Ancestry DID
    }
  };

  const handleSearchSelect = async (member: {
    name: string;
    species: string;
    did?: string;
    gender?: string;
    breed?: string;
    color?: string;
  }) => {
    const relationshipToApiRelation = (rel: string): string => {
      const mapping: Record<string, string> = {
        Mother: 'mother',
        Father: 'father',
        Sibling: 'sibling',
        'Maternal Grandmother': 'maternalGrandmother',
        'Maternal Grandfather': 'maternalGrandfather',
        'Paternal Grandmother': 'paternalGrandmother',
        'Paternal Grandfather': 'paternalGrandfather',
        'Grandmother (Maternal)': 'maternalGrandmother',
        'Grandfather (Maternal)': 'maternalGrandfather',
        'Grandmother (Paternal)': 'paternalGrandmother',
        'Grandfather (Paternal)': 'paternalGrandfather',
      };
      return mapping[rel] || 'sibling';
    };

    try {
      await addFamilyMember(pet.id, {
        name: member.name,
        species: member.species.toLowerCase() as 'dog' | 'cat',
        relation: relationshipToApiRelation(selectedRelationship),
        did: member.did,
        gender: member.gender,
        breed: member.breed,
        color: member.color,
      });

      setShowSearchModal(false);
      setSelectedRelationship('');
    } catch (err) {
      console.error('Failed to add family member from search:', err);
    }
  };

  const handleManualAdd = (memberData: {
    name: string;
    species: string;
    gender?: string;
    breed?: string;
    approximateAge?: string;
    color?: string;
    breederName?: string;
    notes?: string;
  }) => {
    setTempMemberData(memberData);
    setShowManualModal(false);
    setShowAncestryDIDModal(true);
  };

  const handleAncestryDIDDecision = (_createDID: boolean) => {
    setShowAncestryDIDModal(false);
    setShowConfirmModal(true);
  };

  const handleConfirmRelationship = async (
    _confidence: 'certain' | 'estimated' | 'unknown'
  ) => {
    if (!tempMemberData) return;

    const relationshipToApiRelation = (rel: string): string => {
      const mapping: Record<string, string> = {
        Mother: 'mother',
        Father: 'father',
        Sibling: 'sibling',
        'Maternal Grandmother': 'maternalGrandmother',
        'Maternal Grandfather': 'maternalGrandfather',
        'Paternal Grandmother': 'paternalGrandmother',
        'Paternal Grandfather': 'paternalGrandfather',
        'Grandmother (Maternal)': 'maternalGrandmother',
        'Grandfather (Maternal)': 'maternalGrandfather',
        'Grandmother (Paternal)': 'paternalGrandmother',
        'Grandfather (Paternal)': 'paternalGrandfather',
      };
      return mapping[rel] || 'sibling';
    };

    try {
      await addFamilyMember(pet.id, {
        name: tempMemberData.name,
        species: tempMemberData.species.toLowerCase() as 'dog' | 'cat',
        relation: relationshipToApiRelation(selectedRelationship),
        gender: tempMemberData.gender,
        breed: tempMemberData.breed,
        approximateAge: tempMemberData.approximateAge,
        color: tempMemberData.color,
        breederName: tempMemberData.breederName,
        notes: tempMemberData.notes,
      });

      setShowConfirmModal(false);
      setTempMemberData(null);
      setSelectedRelationship('');
    } catch (err) {
      console.error('Failed to add family member:', err);
      // You might want to show an error message to the user here
    }
  };

  const handleViewMember = (member: FamilyMember) => {
    setSelectedMember(member);
    setShowViewModal(true);
  };

  const handleEditMember = (member: FamilyMember) => {
    const fullPet = getPetById(member.id);
    if (fullPet) {
      setSelectedPetId(member.id);
      setShowViewModal(false);
      setSelectedMember(null);
      navigate({ to: '/dashboard' });
    }
  };

  const handleRemoveMember = async (_memberId: string) => {
    if (!selectedMember) return;

    const relationshipToApiRelation = (rel: string): string => {
      const mapping: Record<string, string> = {
        Mother: 'mother',
        Father: 'father',
        Sibling: 'sibling',
        'Maternal Grandmother': 'maternalGrandmother',
        'Maternal Grandfather': 'maternalGrandfather',
        'Paternal Grandmother': 'paternalGrandmother',
        'Paternal Grandfather': 'paternalGrandfather',
        'Grandmother (Maternal)': 'maternalGrandmother',
        'Grandfather (Maternal)': 'maternalGrandfather',
        'Grandmother (Paternal)': 'paternalGrandmother',
        'Grandfather (Paternal)': 'paternalGrandfather',
      };
      return mapping[rel] || 'sibling';
    };

    try {
      const relation = relationshipToApiRelation(selectedMember.relationship);
      await removeFamilyMember(pet.id, {
        relation,
        siblingDid: relation === 'sibling' ? selectedMember.did : undefined,
      });
      setShowViewModal(false);
      setSelectedMember(null);
    } catch (err) {
      console.error('Failed to remove family member:', err);
    }
  };

  const handleUpgradeToDID = (memberId: string) => {
    const member = familyMembers.find((m) => m.id === memberId);
    if (member) {
      const ancestryDid = `did:petso:ancestry:0x${Math.random().toString(16).substr(2, 40)}`;
      setFamilyMembers(
        familyMembers.map((m) =>
          m.id === memberId
            ? { ...m, ancestryDid, verificationType: 'ancestry-did' as const }
            : m
        )
      );
    }
  };

  const getVerificationBadge = (member: FamilyMember) => {
    switch (member.verificationType) {
      case 'verified':
        return (
          <div className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 px-2 py-1 rounded">
            <CheckCircle className="w-3 h-3" />
            {t('family.verifiedBadge')}
          </div>
        );
      case 'ancestry-did':
        return (
          <div className="inline-flex items-center gap-1 text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 px-2 py-1 rounded">
            <Shield className="w-3 h-3" />
            {t('family.ancestryDidBadge')}
          </div>
        );
      case 'owner-added':
        return (
          <div className="inline-flex items-center gap-1 text-xs bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300 px-2 py-1 rounded">
            <AlertCircle className="w-3 h-3" />
            {t('family.ownerAddedBadge')}
          </div>
        );
    }
  };

  /** Status badge + optional top-right icon for deceased/lost cards. Never removes pet from tree. */
  const getStatusBadge = (status: PetStatusType) => {
    if (status === 'deceased') {
      return (
        <div className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 px-2 py-1 rounded">
          <span aria-hidden>🕊️</span>
          {t('family.statusDeceased', 'In Loving Memory')}
        </div>
      );
    }
    if (status === 'lost') {
      return (
        <div className="inline-flex items-center gap-1.5 text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/60 dark:text-red-100 px-2.5 py-1 rounded-md border border-red-400/50 dark:border-red-500/50">
          <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
          {t('family.statusMissing', 'Missing')}
        </div>
      );
    }
    return null;
  };

  const getCardStatusClasses = (status: PetStatusType) => {
    if (status === 'deceased') {
      return "relative overflow-hidden bg-gray-50/80 dark:bg-gray-900/50 before:content-[''] before:absolute before:inset-0 before:rounded-xl before:bg-white/15 dark:before:bg-black/20 before:pointer-events-none";
    }
    if (status === 'lost') {
      return 'relative overflow-hidden border-2 border-red-400 dark:border-red-500 ring-2 ring-red-400/50 dark:ring-red-500/50 ring-offset-2 ring-offset-white dark:ring-offset-[#16151D]';
    }
    return '';
  };

  const parents = familyMembers.filter((m) => m.generation === -1);
  const grandparents = familyMembers.filter((m) => m.generation === -2);
  const siblings = familyMembers.filter((m) => m.generation === 0);

  /** Map internal relationship string to translated label for display */
  const getRelationshipLabel = (relationship: string): string => {
    const keyMap: Record<string, string> = {
      Mother: 'family.relationships.mother',
      Father: 'family.relationships.father',
      Sibling: 'family.relationships.sibling',
      'Current Pet': 'family.relationships.currentPet',
      Offspring: 'family.relationships.offspring',
      'Maternal Grandmother': 'family.relationships.maternalGrandmother',
      'Grandmother (Maternal)': 'family.relationships.maternalGrandmother',
      'Maternal Grandfather': 'family.relationships.maternalGrandfather',
      'Grandfather (Maternal)': 'family.relationships.maternalGrandfather',
      'Paternal Grandmother': 'family.relationships.paternalGrandmother',
      'Grandmother (Paternal)': 'family.relationships.paternalGrandmother',
      'Paternal Grandfather': 'family.relationships.paternalGrandfather',
      'Grandfather (Paternal)': 'family.relationships.paternalGrandfather',
    };
    const key = keyMap[relationship];
    return key ? t(key) : relationship;
  };

  const treeStatus =
    familyMembers.length === 0
      ? 'empty'
      : familyMembers.length < 4
        ? 'partial'
        : 'complete';

  // Single ordered list for responsive grid: siblings (left half), current pet (middle), siblings (right half)
  const currentAndSiblingsGridItems = useMemo(() => {
    const mid = Math.floor(siblings.length / 2);
    const left = siblings
      .slice(0, mid)
      .map((member) => ({ type: 'sibling' as const, member }));
    const right = siblings
      .slice(mid)
      .map((member) => ({ type: 'sibling' as const, member }));
    return [...left, { type: 'current' as const }, ...right];
  }, [siblings]);

  // Current pet as FamilyMember so clicking its card opens Family Member Details
  const currentPetAsMember: FamilyMember = {
    id: pet.id,
    did: pet.did,
    name: pet.name,
    species: pet.species,
    breed: pet.breed,
    relationship: 'Current Pet',
    generation: 0,
    verificationType: 'verified',
    photoKey: pet.photoKey,
    imageUrl: pet.imageUrl,
  };

  return (
    <div className="max-w-6xl mx-auto px-3 sm:px-4 min-w-0">
      <div className="flex flex-col gap-4 mb-6 sm:mb-8 sm:flex-row sm:flex-wrap sm:justify-between sm:items-start">
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-3xl mb-2 dark:text-white">{t('family.title')}</h1>
          <div className="flex items-center gap-2">
            {treeStatus === 'empty' && <span className="text-2xl">🌱</span>}
            {treeStatus === 'partial' && <span className="text-2xl">🌿</span>}
            {treeStatus === 'complete' && <span className="text-2xl">🌳</span>}
            <span className="text-gray-600 dark:text-white/70 text-sm">
              {t('family.subtitle')}
            </span>
          </div>
        </div>

        {/* Privacy Controls */}
        <div className="bg-white dark:bg-[#16151D] rounded-lg shadow-md p-4 border border-gray-200 dark:border-white/8 w-full sm:w-auto min-w-0">
          <div className="flex items-center gap-2 mb-2 text-sm text-gray-700 dark:text-white/70">
            <Lock className="w-4 h-4" />
            {t('family.privacyLevel')}
          </div>
          <select
            value={privacyLevel}
            onChange={(e) =>
              setPrivacyLevel(e.target.value as 'private' | 'family' | 'public')
            }
            className="text-sm border border-gray-300 dark:border-white/8 rounded px-3 py-1 dark:bg-[#0F0E16] dark:text-white"
          >
            <option value="private">{t('family.privateOnlyMe')}</option>
            <option value="family">{t('family.familyPetFamily')}</option>
            <option value="public">{t('family.public')}</option>
          </select>
        </div>
      </div>

      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl shadow-md p-4 sm:p-6 mb-6 sm:mb-8 text-white min-w-0">
        <div className="flex items-center gap-3 mb-4">
          <Share2 className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" />
          <h3 className="text-lg sm:text-xl">{t('family.didBasedAncestryLinking')}</h3>
        </div>
        <p className="text-sm opacity-90 break-words">{t('family.buildTreeDescription')}</p>
      </div>

      {/* Current Pet */}
      <div className="mb-12">
        <div className="text-center mb-6">
          <div className="inline-block bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-lg text-sm mb-2">
            {siblings.length > 0
              ? t('family.currentPetAndSiblings')
              : t('family.currentPet')}
          </div>
        </div>

        {/* Responsive grid; when only one card, use flex to center it */}
        <div
          className={
            currentAndSiblingsGridItems.length === 1
              ? 'flex justify-center max-w-5xl mx-auto w-full'
              : 'grid gap-4 sm:gap-5 max-w-5xl mx-auto w-full'
          }
          style={
            currentAndSiblingsGridItems.length === 1
              ? undefined
              : {
                  gridTemplateColumns:
                    'repeat(auto-fill, minmax(min(100%, 12rem), 1fr))',
                }
          }
        >
          {currentAndSiblingsGridItems.map((item) => {
            if (item.type === 'current') {
              // Use pet from context so lost/deceased status stays in sync with Overview
              const currentPetForStatus = getPetById(pet.id) ?? pet;
              const status = getPetStatus(currentPetForStatus);
              const statusClasses = getCardStatusClasses(status);
              return (
                <div
                  key="current-pet"
                  onClick={() => handleViewMember(currentPetAsMember)}
                  className={`bg-white dark:bg-[#16151D] rounded-xl shadow-md p-4 cursor-pointer hover:shadow-lg transition-shadow border-2 border-blue-500 dark:border-blue-600 min-w-0 ${statusClasses}`}
                >
                  {status === 'deceased' && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span
                          className="absolute top-2 right-2 text-lg opacity-90 z-10"
                          aria-hidden
                        >
                          🕊️
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="left" className="max-w-[200px]">
                        {t(
                          'family.deceasedTooltip',
                          'This pet has passed away but remains part of the family.'
                        )}
                      </TooltipContent>
                    </Tooltip>
                  )}
                  {status === 'lost' && (
                    <span
                      className="absolute top-2 right-2 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-red-500/90 dark:bg-red-500/90 text-white"
                      aria-hidden
                      title={t('family.statusMissing', 'Missing')}
                    >
                      <MapPin className="h-3.5 w-3.5" />
                    </span>
                  )}
                  <div className="flex flex-col items-center mb-2">
                    <FamilyMemberAvatar
                      member={{ ...pet, isCurrentPet: true }}
                      size="md"
                      deceased={status === 'deceased'}
                    />
                    <h4 className="text-sm text-center mt-2 mb-1 dark:text-white">
                      {pet.name}
                    </h4>
                    <div className="text-xs text-gray-500 dark:text-white/50 mb-2">
                      {t('family.currentPet')}
                    </div>
                    <div className="flex flex-wrap items-center justify-center gap-1.5">
                      {getVerificationBadge(currentPetAsMember)}
                      {getStatusBadge(status)}
                    </div>
                  </div>
                  <div
                    className="font-mono text-xs text-gray-500 dark:text-white/50 break-all w-full text-center mb-2 min-w-0 overflow-hidden"
                    title={pet.did}
                  >
                    {pet.did || '—'}
                  </div>
                  <div className="flex justify-center mt-2 pt-2 border-t border-gray-100 dark:border-white/5">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setRecordBirthPet(pet);
                            setRecordBirthInitialParentRole(null);
                            setShowRecordBirthModal(true);
                          }}
                          disabled={isDeceased}
                          className="p-2 rounded-lg text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          aria-label={t(
                            'family.recordBirth',
                            'Record Birth / Litter'
                          )}
                        >
                          <Baby className="w-4 h-4" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="max-w-[220px]">
                        {t(
                          'family.recordBirthTooltip',
                          'Record Birth / Litter – add puppies born from this pet and link them to the family tree.'
                        )}
                      </TooltipContent>
                    </Tooltip>
                  </div>
                </div>
              );
            }
            const sibling = item.member;
            const siblingPet = !sibling.id.startsWith('external-')
              ? pets.find((p) => p.id === sibling.id)
              : undefined;
            const canRecordBirthSibling =
              siblingPet && siblingPet.isDeceased !== true;
            const status = getPetStatus(siblingPet);
            const statusClasses = getCardStatusClasses(status);
            return (
              <div
                key={sibling.id}
                onClick={() => handleViewMember(sibling)}
                className={`bg-white dark:bg-[#16151D] rounded-xl shadow-md p-4 cursor-pointer hover:shadow-lg transition-shadow border dark:border-white/8 min-w-0 ${statusClasses}`}
              >
                {status === 'deceased' && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span
                        className="absolute top-2 right-2 text-lg opacity-90 z-10"
                        aria-hidden
                      >
                        🕊️
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="left" className="max-w-[200px]">
                      {t(
                        'family.deceasedTooltip',
                        'This pet has passed away but remains part of the family.'
                      )}
                    </TooltipContent>
                  </Tooltip>
                )}
                {status === 'lost' && (
                  <span
                    className="absolute top-2 right-2 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-red-500/90 dark:bg-red-500/90 text-white"
                    aria-hidden
                    title={t('family.statusMissing', 'Missing')}
                  >
                    <MapPin className="h-3.5 w-3.5" />
                  </span>
                )}
                <div className="flex flex-col items-center mb-2">
                  <FamilyMemberAvatar
                    member={sibling}
                    size="md"
                    deceased={status === 'deceased'}
                  />
                  <h4 className="text-sm text-center mt-2 mb-1 dark:text-white">
                    {sibling.name}
                  </h4>
                  <div className="text-xs text-gray-500 dark:text-white/50 mb-2">
                    {t('family.relationships.sibling')}
                  </div>
                  <div className="flex flex-wrap items-center justify-center gap-1.5">
                    {getVerificationBadge(sibling)}
                    {getStatusBadge(status)}
                  </div>
                </div>
                <div
                  className="font-mono text-xs text-gray-500 dark:text-white/50 break-all w-full text-center mb-2 min-w-0 overflow-hidden"
                  title={sibling.did}
                >
                  {sibling.did || '—'}
                </div>
                <div className="flex justify-center mt-2 pt-2 border-t border-gray-100 dark:border-white/5">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (siblingPet) {
                            setRecordBirthPet(siblingPet);
                            setRecordBirthInitialParentRole(null);
                            setShowRecordBirthModal(true);
                          }
                        }}
                        disabled={!canRecordBirthSibling}
                        className="p-2 rounded-lg text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        aria-label={t(
                          'family.recordBirth',
                          'Record Birth / Litter'
                        )}
                      >
                        <Baby className="w-4 h-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-[220px]">
                      {t(
                        'family.recordBirthTooltip',
                        'Record Birth / Litter – add puppies born from this pet and link them to the family tree.'
                      )}
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>
            );
          })}
        </div>

        {/* Add Sibling Button */}
        <div className="text-center mt-6">
          <button
            onClick={() => handleAddClick('Sibling')}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 bg-white dark:bg-[#16151D] border-2 border-dashed border-gray-300 dark:border-white/8 rounded-lg hover:border-teal-500 dark:hover:border-teal-700/30 hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-all"
          >
            <Plus className="w-5 h-5 text-gray-400 dark:text-white/40" />
            <span className="text-gray-600 dark:text-white/70">
              {t('family.addSibling')}
            </span>
          </button>
        </div>

        {/* Offspring (Puppies / Children) */}
        {offspring.length > 0 && (
          <div className="mt-10 w-full">
            <div className="text-center mb-4">
              <div className="inline-block bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 px-4 py-2 rounded-lg text-sm font-medium">
                {t('family.relationships.offspring', 'Offspring')} (
                {offspring.length})
              </div>
            </div>
            <div className="flex flex-wrap justify-center gap-4">
              {offspring.map((child) => {
                const childAsMember: FamilyMember = {
                  id: child.id,
                  did: child.did,
                  name: child.name,
                  species: child.species,
                  breed: child.breed,
                  relationship: 'Offspring',
                  generation: 1,
                  verificationType: 'verified',
                  photoKey: child.photoKey,
                  imageUrl: child.imageUrl,
                };
                const canRecordBirthOffspring = child.isDeceased !== true;
                const status = getPetStatus(child);
                const statusClasses = getCardStatusClasses(status);
                return (
                  <div
                    key={child.id}
                    onClick={() => handleViewMember(childAsMember)}
                    className={`bg-white dark:bg-[#16151D] rounded-xl shadow-md p-4 w-48 cursor-pointer hover:shadow-lg transition-all border dark:border-white/8 hover:border-amber-400/50 dark:hover:border-amber-500/30 min-w-0 ${statusClasses}`}
                  >
                    {status === 'deceased' && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span
                            className="absolute top-2 right-2 text-lg opacity-90 z-10"
                            aria-hidden
                          >
                            🕊️
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="left" className="max-w-[200px]">
                          {t(
                            'family.deceasedTooltip',
                            'This pet has passed away but remains part of the family.'
                          )}
                        </TooltipContent>
                      </Tooltip>
                    )}
                    {status === 'lost' && (
                      <span
                        className="absolute top-2 right-2 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-red-500/90 dark:bg-red-500/90 text-white"
                        aria-hidden
                        title={t('family.statusMissing', 'Missing')}
                      >
                        <MapPin className="h-3.5 w-3.5" />
                      </span>
                    )}
                    <div className="flex flex-col items-center mb-2">
                      <FamilyMemberAvatar
                        member={{
                          ...child,
                          relationship: 'Offspring',
                          generation: 1,
                        }}
                        size="md"
                        deceased={status === 'deceased'}
                      />
                      <h4 className="text-sm text-center mt-2 mb-1 dark:text-white font-medium truncate w-full px-1">
                        {child.name}
                      </h4>
                      <div
                        className="font-mono text-xs text-gray-500 dark:text-white/50 break-all w-full text-center min-w-0 overflow-hidden"
                        title={child.did}
                      >
                        {child.did || '—'}
                      </div>
                      <div className="mt-2 flex flex-wrap items-center justify-center gap-1.5">
                        {status === 'active' ? (
                          child.ownershipStatus === 'pending_transfer' ? (
                            <span className="inline-flex items-center gap-1 text-xs bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 px-2 py-0.5 rounded">
                              🟡{' '}
                              {t(
                                'family.birth.pendingTransfer',
                                'Pending Transfer'
                              )}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 px-2 py-0.5 rounded">
                              🟢 {t('family.birth.active', 'Active')}
                            </span>
                          )
                        ) : (
                          getStatusBadge(status)
                        )}
                      </div>
                    </div>
                    <div className="flex justify-center mt-2 pt-2 border-t border-gray-100 dark:border-white/5">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setRecordBirthPet(child);
                              setRecordBirthInitialParentRole(null);
                              setShowRecordBirthModal(true);
                            }}
                            disabled={!canRecordBirthOffspring}
                            className="p-2 rounded-lg text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            aria-label={t(
                              'family.recordBirth',
                              'Record Birth / Litter'
                            )}
                          >
                            <Baby className="w-4 h-4" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="max-w-[220px]">
                          {t(
                            'family.recordBirthTooltip',
                            'Record Birth / Litter – add puppies born from this pet and link them to the family tree.'
                          )}
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Parents Generation */}
      <div className="relative mb-12">
        <div className="text-center mb-6">
          <div className="inline-block bg-gray-200 dark:bg-[#0F0E16] text-gray-700 dark:text-white/70 px-4 py-2 rounded-lg text-sm">
            {t('family.parents')}
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4 sm:gap-8 max-w-4xl mx-auto">
          {parents.map((member) => {
            const parentPet = !member.id.startsWith('external-')
              ? pets.find((p) => p.id === member.id)
              : undefined;
            const canRecordBirth = parentPet && parentPet.isDeceased !== true;
            const status = getPetStatus(parentPet);
            const statusClasses = getCardStatusClasses(status);
            return (
              <div
                key={member.id}
                onClick={() => handleViewMember(member)}
                className={`bg-white dark:bg-[#16151D] rounded-xl shadow-md p-4 sm:p-6 cursor-pointer hover:shadow-lg transition-shadow border dark:border-white/8 relative overflow-hidden min-w-0 ${statusClasses}`}
              >
                {status === 'deceased' && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span
                        className="absolute top-3 right-3 text-xl opacity-90 z-10"
                        aria-hidden
                      >
                        🕊️
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="left" className="max-w-[200px]">
                      {t(
                        'family.deceasedTooltip',
                        'This pet has passed away but remains part of the family.'
                      )}
                    </TooltipContent>
                  </Tooltip>
                )}
                {status === 'lost' && (
                  <span
                    className="absolute top-3 right-3 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-red-500/90 dark:bg-red-500/90 text-white"
                    aria-hidden
                    title={t('family.statusMissing', 'Missing')}
                  >
                    <MapPin className="h-4 w-4" />
                  </span>
                )}
                <div className="flex items-start justify-between gap-2 mb-3 min-w-0">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <FamilyMemberAvatar
                      member={member}
                      size="md"
                      deceased={status === 'deceased'}
                    />
                    <div className="min-w-0">
                      <h4 className="mb-1 dark:text-white truncate">{member.name}</h4>
                      <div className="text-sm text-gray-500 dark:text-white/50">
                        {getRelationshipLabel(member.relationship)}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 justify-end">
                    {getVerificationBadge(member)}
                    {getStatusBadge(status)}
                  </div>
                </div>
                <div className="text-sm text-gray-600 dark:text-white/70 mb-3">
                  {member.breed}
                </div>
                {(member.did || member.ancestryDid) && (
                  <div className="bg-gray-50 dark:bg-[#0F0E16] rounded-lg p-2 mb-3 min-w-0 overflow-hidden">
                    <div className="text-xs text-gray-500 dark:text-white/50 mb-1">
                      {member.verificationType === 'verified'
                        ? t('family.verifiedDid')
                        : t('family.ancestryDid')}
                    </div>
                    <div className="font-mono text-xs break-all dark:text-white/70">
                      {member.did || member.ancestryDid}
                    </div>
                  </div>
                )}
                {member.verificationType === 'verified' && (
                  <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400 mb-3">
                    <LinkIcon className="w-4 h-4" />
                    <span>{t('family.blockchainVerified')}</span>
                  </div>
                )}
                {/* Record Birth icon below Verified / content */}
                <div className="flex justify-end pt-2 border-t border-gray-100 dark:border-white/5">
                  {canRecordBirth ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (parentPet) {
                              setRecordBirthPet(parentPet);
                              setRecordBirthInitialParentRole(
                                member.relationship.includes('Mother')
                                  ? 'mother'
                                  : 'father'
                              );
                              setShowRecordBirthModal(true);
                            }
                          }}
                          className="p-2 rounded-lg text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/30 transition-colors"
                          aria-label={t(
                            'family.recordBirth',
                            'Record Birth / Litter'
                          )}
                        >
                          <Baby className="w-4 h-4" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="max-w-[220px]">
                        {t(
                          'family.recordBirthTooltip',
                          'Record Birth / Litter – add puppies born from this pet and link them to the family tree.'
                        )}
                      </TooltipContent>
                    </Tooltip>
                  ) : null}
                </div>
              </div>
            );
          })}

          {/* Add Parent Buttons */}
          {parents.length < 2 && (
            <>
              {!parents.find((p) => p.relationship.includes('Mother')) && (
                <button
                  onClick={() => handleAddClick('Mother')}
                  className="w-full bg-white dark:bg-[#16151D] border-2 border-dashed border-gray-300 dark:border-white/8 rounded-xl p-4 sm:p-6 hover:border-blue-500 dark:hover:border-blue-700/30 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all min-w-0"
                >
                  <div className="flex flex-col items-center gap-3">
                    <Plus className="w-8 h-8 text-gray-400 dark:text-white/40" />
                    <span className="text-gray-600 dark:text-white/70">
                      {t('family.addMother')}
                    </span>
                  </div>
                </button>
              )}
              {!parents.find((p) => p.relationship.includes('Father')) && (
                <button
                  onClick={() => handleAddClick('Father')}
                  className="w-full bg-white dark:bg-[#16151D] border-2 border-dashed border-gray-300 dark:border-white/8 rounded-xl p-4 sm:p-6 hover:border-blue-500 dark:hover:border-blue-700/30 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all min-w-0"
                >
                  <div className="flex flex-col items-center gap-3">
                    <Plus className="w-8 h-8 text-gray-400 dark:text-white/40" />
                    <span className="text-gray-600 dark:text-white/70">
                      {t('family.addFather')}
                    </span>
                  </div>
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Grandparents Generation */}
      {(grandparents.length > 0 || parents.length === 2) && (
        <div className="relative mb-12">
          <div className="text-center mb-6">
            <div className="inline-block bg-gray-200 dark:bg-[#0F0E16] text-gray-700 dark:text-white/70 px-4 py-2 rounded-lg text-sm">
              {t('family.grandparents')}
            </div>
          </div>
          <div className="grid md:grid-cols-4 gap-4 max-w-6xl mx-auto">
            {grandparents.map((member) => {
              const grandparentPet = !member.id.startsWith('external-')
                ? pets.find((p) => p.id === member.id)
                : undefined;
              const status = getPetStatus(grandparentPet);
              const statusClasses = getCardStatusClasses(status);
              return (
                <div
                  key={member.id}
                  onClick={() => handleViewMember(member)}
                  className={`bg-white dark:bg-[#16151D] rounded-xl shadow-md p-4 cursor-pointer hover:shadow-lg transition-all border dark:border-white/8 relative overflow-hidden ${status === 'active' ? 'opacity-90 hover:opacity-100' : ''} ${statusClasses}`}
                >
                  {status === 'deceased' && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span
                          className="absolute top-2 right-2 text-base opacity-90 z-10"
                          aria-hidden
                        >
                          🕊️
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="left" className="max-w-[200px]">
                        {t(
                          'family.deceasedTooltip',
                          'This pet has passed away but remains part of the family.'
                        )}
                      </TooltipContent>
                    </Tooltip>
                  )}
                  {status === 'lost' && (
                    <span
                      className="absolute top-2 right-2 z-10 flex h-5 w-5 items-center justify-center rounded-full bg-red-500/90 dark:bg-red-500/90 text-white"
                      aria-hidden
                      title={t('family.statusMissing', 'Missing')}
                    >
                      <MapPin className="h-3 w-3" />
                    </span>
                  )}
                  <div className="flex flex-col items-center text-center mb-3">
                    <FamilyMemberAvatar
                      member={member}
                      size="sm"
                      deceased={status === 'deceased'}
                    />
                    <h4 className="text-sm mt-2 mb-1 dark:text-white">
                      {member.name}
                    </h4>
                    <div className="text-xs text-gray-500 dark:text-white/50 mb-2">
                      {getRelationshipLabel(member.relationship)}
                    </div>
                    <div className="flex flex-wrap items-center justify-center gap-1.5">
                      {getVerificationBadge(member)}
                      {getStatusBadge(status)}
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Add Grandparent Buttons */}
            {grandparents.length < 4 && parents.length === 2 && (
              <>
                {!grandparents.find(
                  (g) =>
                    g.relationship === 'Maternal Grandmother' ||
                    g.relationship === 'Grandmother (Maternal)'
                ) && (
                  <button
                    onClick={() => handleAddClick('Maternal Grandmother')}
                    className="bg-white dark:bg-[#16151D] border-2 border-dashed border-gray-300 dark:border-white/8 rounded-xl p-4 hover:border-purple-500 dark:hover:border-purple-700/30 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-all"
                  >
                    <div className="flex flex-col items-center gap-2">
                      <Plus className="w-6 h-6 text-gray-400 dark:text-white/40" />
                      <span className="text-xs text-gray-600 dark:text-white/70 text-center">
                        {t('family.addMaternalGrandmother')}
                      </span>
                    </div>
                  </button>
                )}
                {!grandparents.find(
                  (g) =>
                    g.relationship === 'Maternal Grandfather' ||
                    g.relationship === 'Grandfather (Maternal)'
                ) && (
                  <button
                    onClick={() => handleAddClick('Maternal Grandfather')}
                    className="bg-white dark:bg-[#16151D] border-2 border-dashed border-gray-300 dark:border-white/8 rounded-xl p-4 hover:border-purple-500 dark:hover:border-purple-700/30 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-all"
                  >
                    <div className="flex flex-col items-center gap-2">
                      <Plus className="w-6 h-6 text-gray-400 dark:text-white/40" />
                      <span className="text-xs text-gray-600 dark:text-white/70 text-center">
                        {t('family.addMaternalGrandfather')}
                      </span>
                    </div>
                  </button>
                )}
                {!grandparents.find(
                  (g) =>
                    g.relationship === 'Paternal Grandmother' ||
                    g.relationship === 'Grandmother (Paternal)'
                ) && (
                  <button
                    onClick={() => handleAddClick('Paternal Grandmother')}
                    className="bg-white dark:bg-[#16151D] border-2 border-dashed border-gray-300 dark:border-white/8 rounded-xl p-4 hover:border-purple-500 dark:hover:border-purple-700/30 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-all"
                  >
                    <div className="flex flex-col items-center gap-2">
                      <Plus className="w-6 h-6 text-gray-400 dark:text-white/40" />
                      <span className="text-xs text-gray-600 dark:text-white/70 text-center">
                        {t('family.addPaternalGrandmother')}
                      </span>
                    </div>
                  </button>
                )}
                {!grandparents.find(
                  (g) =>
                    g.relationship === 'Paternal Grandfather' ||
                    g.relationship === 'Grandfather (Paternal)'
                ) && (
                  <button
                    onClick={() => handleAddClick('Paternal Grandfather')}
                    className="bg-white dark:bg-[#16151D] border-2 border-dashed border-gray-300 dark:border-white/8 rounded-xl p-4 hover:border-purple-500 dark:hover:border-purple-700/30 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-all"
                  >
                    <div className="flex flex-col items-center gap-2">
                      <Plus className="w-6 h-6 text-gray-400 dark:text-white/40" />
                      <span className="text-xs text-gray-600 dark:text-white/70 text-center">
                        {t('family.addPaternalGrandfather')}
                      </span>
                    </div>
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* DNA & Heritage Section - 2x2 on mobile, 4 cols on large */}
      <div className="bg-white dark:bg-[#16151D] rounded-xl shadow-md p-4 sm:p-6 border dark:border-white/8 min-w-0">
        <div className="flex items-center gap-2 mb-4">
          <Users className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
          <h3 className="text-lg sm:text-xl dark:text-white">{t('family.dnaHeritage')}</h3>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg min-w-0">
            <div className="text-sm text-gray-600 dark:text-blue-300 mb-2">
              {t('family.familyMembersCount')}
            </div>
            <div className="text-2xl mb-2 dark:text-white">
              {familyMembers.length}
            </div>
            <div className="text-xs text-gray-500 dark:text-blue-400">
              {
                familyMembers.filter((m) => m.verificationType === 'verified')
                  .length
              }{' '}
              {t('family.verifiedCount')}
            </div>
          </div>
          <div className="p-4 bg-teal-50 dark:bg-teal-900/20 rounded-lg min-w-0">
            <div className="text-sm text-gray-600 dark:text-teal-300 mb-2">
              {t('family.siblingsCount')}
            </div>
            <div className="text-2xl mb-2 dark:text-white">
              {siblings.length}
            </div>
            <div className="text-xs text-gray-500 dark:text-teal-400">
              {t('family.sameLineage')}
            </div>
          </div>
          <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg min-w-0">
            <div className="text-sm text-gray-600 dark:text-purple-300 mb-2">
              {t('family.ancestryDidsCount')}
            </div>
            <div className="text-2xl mb-2 dark:text-white">
              {
                familyMembers.filter(
                  (m) => m.verificationType === 'ancestry-did'
                ).length
              }
            </div>
            <div className="text-xs text-gray-500 dark:text-purple-400">
              {t('family.placeholderIdentities')}
            </div>
          </div>
          <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg min-w-0">
            <div className="text-sm text-gray-600 dark:text-green-300 mb-2">
              {t('family.treeStatusLabel')}
            </div>
            <div className="text-2xl mb-2 capitalize dark:text-white">
              {treeStatus === 'empty' && t('family.treeStatusValueEmpty')}
              {treeStatus === 'partial' && t('family.treeStatusValuePartial')}
              {treeStatus === 'complete' && t('family.treeStatusValueComplete')}
            </div>
            <div className="text-xs text-gray-500 dark:text-green-400">
              {treeStatus === 'empty' && t('family.treeStatusEmpty')}
              {treeStatus === 'partial' && t('family.treeStatusPartial')}
              {treeStatus === 'complete' && t('family.treeStatusComplete')}
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showAddModal && (
        <AddFamilyMemberModal
          onClose={() => {
            setShowAddModal(false);
            setSelectedRelationship('');
          }}
          onSelectMethod={handleMethodSelect}
          relationship={selectedRelationship}
          relationshipDisplayLabel={getRelationshipLabel(selectedRelationship)}
        />
      )}

      {showSearchModal && (
        <SearchFamilyMemberModal
          onClose={() => {
            setShowSearchModal(false);
            setSelectedRelationship('');
          }}
          onBack={() => {
            setShowSearchModal(false);
            setShowAddModal(true);
          }}
          onSelect={handleSearchSelect}
          relationship={selectedRelationship}
        />
      )}

      {showManualModal && (
        <ManualFamilyMemberModal
          onClose={() => {
            setShowManualModal(false);
            setSelectedRelationship('');
          }}
          onBack={() => {
            setShowManualModal(false);
            setShowAddModal(true);
          }}
          onAdd={handleManualAdd}
          relationship={selectedRelationship}
        />
      )}

      {showAncestryDIDModal && tempMemberData && (
        <CreateAncestryDIDModal
          onClose={() => {
            setShowAncestryDIDModal(false);
            setTempMemberData(null);
            setSelectedRelationship('');
          }}
          onConfirm={handleAncestryDIDDecision}
          memberData={tempMemberData}
          relationship={selectedRelationship}
          relationshipDisplayLabel={getRelationshipLabel(selectedRelationship)}
        />
      )}

      {showConfirmModal && tempMemberData && (
        <ConfirmRelationshipModal
          onClose={() => {
            setShowConfirmModal(false);
            setTempMemberData(null);
            setSelectedRelationship('');
          }}
          onConfirm={handleConfirmRelationship}
          memberData={tempMemberData}
          relationship={selectedRelationship}
          relationshipDisplayLabel={getRelationshipLabel(selectedRelationship)}
        />
      )}

      {showViewModal && selectedMember && (
        <ViewFamilyMemberModal
          member={selectedMember}
          pet={getPetById(selectedMember.id) ?? null}
          onClose={() => {
            setShowViewModal(false);
            setSelectedMember(null);
          }}
          onEdit={handleEditMember}
          onRemove={handleRemoveMember}
          onUpgradeToDID={handleUpgradeToDID}
          onUpdatePet={updatePet}
          onReportLostSuccess={() => refreshPets()}
          onMarkFound={async (petId) => {
            await markFound(petId);
            await refreshPets();
          }}
        />
      )}

      {showRecordBirthModal && recordBirthPet && (
        <RecordBirthModal
          pet={recordBirthPet}
          initialParentRole={recordBirthInitialParentRole ?? undefined}
          onClose={() => {
            setShowRecordBirthModal(false);
            setRecordBirthPet(null);
            setRecordBirthInitialParentRole(null);
          }}
          onSuccess={() => {
            refreshPets();
            setShowRecordBirthModal(false);
            setRecordBirthPet(null);
            setRecordBirthInitialParentRole(null);
          }}
        />
      )}
    </div>
  );
}
